import { useUserStore } from '../../../../features/auth/store/userStore';
import { useAuthStore } from '../../../../features/auth/store/authStore';
import { ROLES, hasRole } from '../../../../shared/constants/roles';
import { hasMultipleSites } from '../../../../shared/utils/multiSite';
import { segmentedTrack, segmentedTab } from '../profileStyles';

const tabs = [
  // Id kept as 'profile' (it was the single Profile tab) so existing test ids stay stable.
  { id: 'profile', label: 'Organization Profile' },
  { id: 'my-profile', label: 'My Profile' },
  { id: 'password', label: 'Password' },
  // Client rule: no saving payment methods for a multi-site firm, so the whole tab goes for one.
  { id: 'payment', label: 'Payment', allowedRoles: [ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN], hideForMultiSite: true },
  { id: 'sso', label: 'SSO', allowedRoles: [ROLES.ACCOUNT_OWNER]},
  { id: 'notifications', label: 'Notifications' },
];

export function ProfileSidebar({ activeTab, onTabChange, disabled = false, dataTestId }) {
  const { roles } = useUserStore();
  const isMultiSiteFirm = hasMultipleSites(useAuthStore((s) => s.selectedCompany?.sites));

  const visibleTabs = tabs.filter((tab) => {
    if (tab.hideForMultiSite && isMultiSiteFirm) {
      return false;
    }
    if (!tab.allowedRoles) {
      return true;
    }
    return tab.allowedRoles.some((role) => hasRole(roles, role));
  });

  return (
    // `border-line` matches the MFA card just below it in this same panel: --surface-field is
    // almost the panel's own colour in light, so without an edge the strip read as four loose
    // buttons rather than one segmented control.
    //
    // Up to six tabs share the drawer's width, so labels may wrap onto two lines ("Organization
    // Profile") and tabs size to their text rather than splitting the width equally. Where even that
    // cannot fit (narrow phones) the strip scrolls sideways instead of spilling out of the drawer.
    <div
      className={`${segmentedTrack} overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
      data-testid={dataTestId}
    >
      {visibleTabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => !disabled && onTabChange(tab.id)}
            disabled={disabled}
            className={segmentedTab({ active: isActive, disabled })}
            data-testid={dataTestId ? `${dataTestId}-tab-${tab.id}` : undefined}
          >
            <span className="text-center">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}
